// useAcquisitionAttribution.ts — CORE-1137
//
// Входная половина marketing attribution: с чем человек пришёл в приложение.
// Модель, правила first/last-touch и приватность живут в
// `services/acquisition.ts` — здесь только то, что зависит от платформы:
// откуда взять адрес и когда его прочитать.
//
// Ставится в `AppContent` РАНЬШЕ авторизационных эффектов и намеренно ничего о
// ней не знает: событие описывает анонимный визит, и ждать `isAuthenticated`
// значило бы читать адрес после того, как React Navigation его перепишет.

import { useEffect } from 'react';
import { Linking, Platform } from 'react-native';
import { analytics } from '../services/analytics';
import { recordVisit, webEntrySnapshot } from '../services/acquisition';
import { splitUrl } from './useShareLinkAttribution';

/**
 * Учесть визит один раз на аналитическую сессию — и ещё раз, если внутри живой
 * сессии пришло новое приписываемое касание (нативный deep link по объявлению).
 */
export function useAcquisitionAttribution(): void {
  useEffect(() => {
    let cancelled = false;

    /**
     * `force` = «строку писать, даже если касание ничего не изменило». Так
     * помечается НАЧАЛО сессии: без этого у вернувшегося напрямую человека
     * сессия осталась бы без единой атрибутирующей строки, и его события
     * выпали бы из разрезов по кампании, хотя источник давно известен.
     */
    const record = async (
      visit: { search?: string | null; pathname?: string | null; referrer?: string | null; selfHost?: string | null },
      force: boolean,
    ) => {
      if (cancelled) return;
      try {
        await recordVisit({ ...visit, nowUtc: new Date().toISOString() }, { force });
      } catch {
        // Атрибуция — наблюдение, а не функциональность: её отказ не должен
        // быть виден человеку, который просто открыл приложение.
      }
    };

    const recordEntry = async (force: boolean) => {
      if (Platform.OS === 'web') {
        const entry = webEntrySnapshot();
        if (entry) await record(entry, force);
        return;
      }
      // Нативный холодный старт: адрес есть только если приложение открыли
      // ссылкой. Его нет — это всё равно визит, и первый из них становится
      // first-touch («прямой») ровно как на вебе.
      const href = await Linking.getInitialURL().catch(() => null);
      const { pathname, search } = splitUrl(href || '/');
      await record({ pathname, search }, force);
    };

    analytics.getSessionInfo()
      .then((info) => recordEntry(info.isNew))
      .catch(() => { /* сессия не поднялась — визит просто не размечен */ });

    // Сессия истекла по бездействию и началась новая, пока приложение открыто.
    const unsubscribe = analytics.onSessionStart(() => { void recordEntry(true); });

    // Тёплый старт по ссылке: приложение уже жило, человек вернулся по новому
    // объявлению. `force: false` — строка появится, только если касание
    // действительно приписываемое, иначе это обычный внутренний deep link.
    let linkSub: { remove: () => void } | null = null;
    if (Platform.OS !== 'web') {
      linkSub = Linking.addEventListener('url', ({ url }) => {
        const { pathname, search } = splitUrl(url || '');
        void record({ pathname, search }, false);
      });
    }

    return () => {
      cancelled = true;
      unsubscribe();
      linkSub?.remove();
    };
  }, []);
}
